import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import {
  CONSENT_CATEGORIES,
  acceptAllConsent,
  getConsent,
  rejectAllConsent,
  saveConsent,
  subscribeToPreferencesRequest,
} from 'utils/cookieConsent';
import { LEGAL_URLS } from 'constants/common';

const deniedByDefault = CONSENT_CATEGORIES.reduce(
  (acc, category) => ({ ...acc, [category.id]: false }),
  {}
);

const PolicyLinks = () => (
  <>
    <Link
      href={LEGAL_URLS.COOKIE_POLICY}
      target="_blank"
      rel="noopener noreferrer"
    >
      Cookie Policy
    </Link>{' '}
    and{' '}
    <Link
      href={LEGAL_URLS.PRIVACY_POLICY}
      target="_blank"
      rel="noopener noreferrer"
    >
      Privacy Policy
    </Link>
  </>
);

const CookieConsentBanner = () => {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selection, setSelection] = useState(deniedByDefault);

  useEffect(() => {
    if (!getConsent()) setBannerVisible(true);
  }, []);

  // Reopening from the footer must show the user's current choices, not defaults.
  const openSettings = useCallback(() => {
    setSelection({ ...deniedByDefault, ...getConsent()?.categories });
    setSettingsOpen(true);
  }, []);

  useEffect(() => subscribeToPreferencesRequest(openSettings), [openSettings]);

  const dismiss = () => {
    setSettingsOpen(false);
    setBannerVisible(false);
  };

  const handleAcceptAll = () => {
    acceptAllConsent();
    dismiss();
  };

  const handleRejectAll = () => {
    rejectAllConsent();
    dismiss();
  };

  const handleSaveSelection = () => {
    saveConsent(selection);
    dismiss();
  };

  const toggleCategory = (id) => (event) =>
    setSelection((current) => ({ ...current, [id]: event.target.checked }));

  return (
    <>
      {bannerVisible && (
        <Paper
          elevation={6}
          role="region"
          aria-label="Cookie consent"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            p: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            We use strictly necessary cookies to run this dashboard, and
            analytics cookies only if you agree. You can change or withdraw your
            choice at any time. See our <PolicyLinks />.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              justifyContent: { xs: 'stretch', md: 'flex-end' },
            }}
          >
            <Button size="small" onClick={openSettings}>
              Cookie settings
            </Button>
            {/* Reject is styled identically to Accept on purpose: consent must
                not be nudged by making refusal the less prominent option. */}
            <Button variant="contained" size="small" onClick={handleRejectAll}>
              Reject non-essential
            </Button>
            <Button variant="contained" size="small" onClick={handleAcceptAll}>
              Accept all
            </Button>
          </Box>
        </Paper>
      )}

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cookie settings</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose which cookies you allow. For details on each cookie we set,
            see our <PolicyLinks />.
          </Typography>

          <FormControlLabel
            control={<Switch checked disabled />}
            label={
              <Box>
                <Typography variant="subtitle2">
                  Strictly necessary cookies
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Required to log you in and keep the dashboard secure. These
                  cannot be turned off.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />

          {CONSENT_CATEGORIES.map((category) => (
            <React.Fragment key={category.id}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(selection[category.id])}
                    onChange={toggleCategory(category.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle2">
                      {category.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </React.Fragment>
          ))}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" onClick={handleRejectAll}>
            Reject non-essential
          </Button>
          <Button size="small" onClick={handleAcceptAll}>
            Accept all
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveSelection}
          >
            Save preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
